<template>
    <div v-if="$auth.check()">
        <v-row align="center">
            <v-col cols="12" sm="6">
                <h1>Visão geral</h1>
            </v-col>
            <v-col cols="12" sm="6" class="text-sm-right">
                <span class="grey--text mr-3" v-if="pending > 0">consultando OLTs ({{ pending }} restantes)...</span>
                <v-btn small color="primary" :disabled="pending > 0" @click="load()">
                    <v-icon left small>mdi-refresh</v-icon>atualizar
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="6" md="3">
                <v-card>
                    <v-card-text>
                        <div class="text-caption">OLTs</div>
                        <div class="text-h4 primary--text">{{ olts.length }}</div>
                        <div class="text-caption">{{ totals.ports }} portas PON</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" md="3">
                <v-card>
                    <v-card-text>
                        <div class="text-caption">ONTs</div>
                        <div class="text-h4 blue-grey--text text--darken-1">{{ totals.onts }}</div>
                        <div class="text-caption">nas OLTs consultadas</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" md="3">
                <v-card>
                    <v-card-text>
                        <div class="text-caption">UP</div>
                        <div class="text-h4 success--text">{{ totals.up }}</div>
                        <div class="text-caption">{{ upRatio | formatPercent }} do total</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" md="3">
                <v-card>
                    <v-card-text>
                        <div class="text-caption">DOWN</div>
                        <div class="text-h4 error--text">{{ totals.down }}</div>
                        <div class="text-caption">{{ downRatio | formatPercent }} do total</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>OLTs</v-card-title>
                    <v-card-text>
                        <v-alert dense text type="info" v-if="olts.length == 0">
                            Nenhuma OLT cadastrada
                        </v-alert>
                        <v-simple-table v-else>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Nome</th>
                                    <th>Host</th>
                                    <th>Interfaces</th>
                                    <th>ONTs</th>
                                    <th>UP</th>
                                    <th>DOWN</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in olts" :key="row.olt.id" :style="bgItem(row)">
                                    <td>{{ row.olt.code }}</td>
                                    <td>{{ row.olt.name }}</td>
                                    <td>{{ row.olt.host }}</td>
                                    <td>{{ row.loading || row.error ? "-" : row.ports }}</td>
                                    <td>
                                        <v-progress-circular indeterminate size="16" width="2" color="primary" v-if="row.loading"></v-progress-circular>
                                        <v-icon small color="error" title="falha ao consultar a OLT" v-else-if="row.error">mdi-alert-circle-outline</v-icon>
                                        <span v-else>{{ row.total }}</span>
                                    </td>
                                    <td>{{ row.loading || row.error ? "-" : row.up }}</td>
                                    <td>{{ row.loading || row.error ? "-" : row.down }}</td>
                                    <td>
                                        <v-btn icon small color="success" title="administração" @click="admin(row.olt.id)">
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-btn>
                                        <v-btn icon small color="warning" title="status" @click="status(row.olt.id)">
                                            <v-icon>mdi-chart-box-outline</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>
                        Interfaces críticas
                        <v-chip class="ml-3" small v-if="criticals.length > 0">{{ criticals.length }}</v-chip>
                    </v-card-title>
                    <v-card-subtitle>portas PON com DOWN acima de 20% das ONTs</v-card-subtitle>
                    <v-card-text>
                        <v-alert dense text type="success" v-if="criticals.length == 0">
                            Nenhuma interface crítica
                        </v-alert>
                        <v-simple-table v-else>
                            <thead>
                                <tr>
                                    <th>OLT</th>
                                    <th>Interface</th>
                                    <th>Total</th>
                                    <th>UP</th>
                                    <th>DOWN</th>
                                    <th>% DOWN</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in criticals" :key="index" :style="bgItem(item)">
                                    <td>{{ item.olt.name }}</td>
                                    <td>{{ item.name }}</td>
                                    <td>{{ item.total }}</td>
                                    <td>{{ item.up }}</td>
                                    <td>{{ item.down }}</td>
                                    <td>{{ item.ratio | formatPercent }}</td>
                                    <td>
                                        <v-btn icon small color="warning" title="status" @click="status(item.olt.id)">
                                            <v-icon>mdi-chart-box-outline</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import api from "../api/index.js";
import router from "../router/index.js";
export default {
    data: () => ({
        olts: [],
        pending: 0
    }),
    created: function() {
        this.load();
    },
    computed: {
        totals: function() {
            var ret = { onts: 0, up: 0, down: 0, ports: 0 };
            for (var i = 0; i < this.olts.length; i++) {
                ret.onts += this.olts[i].total;
                ret.up += this.olts[i].up;
                ret.down += this.olts[i].down;
                ret.ports += this.olts[i].ports;
            }
            return ret;
        },
        upRatio: function() {
            return this.totals.onts > 0 ? this.totals.up / this.totals.onts : null;
        },
        downRatio: function() {
            return this.totals.onts > 0 ? this.totals.down / this.totals.onts : null;
        },
        criticals: function() {
            var ret = [];
            for (var i = 0; i < this.olts.length; i++) {
                var row = this.olts[i];
                for (var j = 0; j < row.interfaces.length; j++) {
                    var item = row.interfaces[j];
                    if (item.total == 0) {
                        continue;
                    }
                    var down = item.inactive + item.invalid;
                    var ratio = down / item.total;
                    //mesmo limite de atencao usado no status da OLT
                    if (ratio > 0.2) {
                        ret.push({ olt: row.olt, name: item.name, total: item.total, up: item.active, down: down, ratio: ratio });
                    }
                }
            }
            //as piores primeiro
            ret.sort(function(a, b) {
                return b.ratio - a.ratio;
            });
            return ret;
        }
    },
    methods: {
        load: function() {
            var me = this;
            var token = localStorage.getItem("token");
            api
                .get("/olt", { headers: { Authorization: "Bearer " + token } })
                .then(function(response) {
                    //monta as linhas com os contadores ja declarados para manterem a reatividade
                    me.olts = response.data.map(function(olt) {
                        return { olt: olt, total: 0, up: 0, down: 0, ports: 0, interfaces: [], loading: true, error: false };
                    });
                    for (var i = 0; i < me.olts.length; i++) {
                        me.loadStatus(me.olts[i]);
                    }
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        loadStatus: function(row) {
            var me = this;
            var token = localStorage.getItem("token");
            me.pending++;
            api
                .get("/olt/status/" + row.olt.id, { headers: { Authorization: "Bearer " + token } })
                .then(function(response) {
                    var list = response.data || [];
                    for (var i = 0; i < list.length; i++) {
                        row.total += list[i].total;
                        row.up += list[i].active;
                        row.down += list[i].inactive + list[i].invalid;
                    }
                    row.ports = list.length;
                    row.interfaces = list;
                })
                .catch(function(error) {
                    row.error = true;
                    console.log(error);
                })
                .finally(function() {
                    row.loading = false;
                    me.pending--;
                });
        },
        bgItem: function(row) {
            if (row.loading || row.error || row.total == 0) {
                return;
            }
            //mesmos limites usados no status da OLT
            var dp = row.down / row.total;
            if (dp > 0.4) {
                return {
                    backgroundColor: "#ff6961"
                };
            } else if (dp > 0.2) {
                return {
                    backgroundColor: "#fdfd96"
                };
            }
        },
        admin: function(id) {
            router.push({ path: `/olt/admin/${id}` });
        },
        status: function(id) {
            router.push({ path: `/olt/status/${id}` });
        }
    },
    filters: {
        formatPercent: function(value) {
            if (value === null || isNaN(value)) {
                return "-";
            }
            return (value * 100).toFixed(1).replace(".", ",") + "%";
        }
    }
};
</script>
